import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface CartBadgeProps {
  itemCount: number;
}

export function CartBadge({ itemCount }: CartBadgeProps) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <div className="relative shrink-0">
      <NavLink
        to="/cart"
        className={({ isActive }) =>
          `relative flex items-center justify-center h-auto w-auto p-4 transition-colors ${
            isActive ? 'text-[#0000FF]' : 'text-foreground hover:text-[#0000FF]'
          }`
        }
        aria-label={`Shopping cart with ${itemCount} items`}
      >
        {({ isActive }) => (
          <>
            <ShoppingCart className="h-7 w-7" />
            {isActive && (
              <motion.div
                layoutId="activeCartIndicator"
                className="absolute bottom-0 left-0 right-0 h-0.5"
                style={{ backgroundColor: '#0000FF' }}
                transition={
                  prefersReducedMotion
                    ? { duration: 0 }
                    : { type: 'spring', stiffness: 380, damping: 30 }
                }
              />
            )}
          </>
        )}
      </NavLink>
      <AnimatePresence>
        {itemCount > 0 && (
          <motion.div
            initial={prefersReducedMotion ? { opacity: 1 } : { scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { scale: 0, opacity: 0 }}
            transition={
              prefersReducedMotion
                ? { duration: 0.1 }
                : { type: 'spring', stiffness: 500, damping: 25 }
            }
            className="absolute top-0 right-0 -translate-y-1 translate-x-1 z-20 pointer-events-none"
          >
            <Badge
              className="h-5 min-w-[20px] flex items-center justify-center p-0 text-xs font-bold"
              style={{ backgroundColor: '#0000FF', color: 'white' }}
            >
              {itemCount > 99 ? '99+' : itemCount}
            </Badge>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

